import { useEffect, useState, useMemo, useContext } from "react";
import { getInlineLoader } from "../Helpers";
import banner1 from "../assets/banner1.png";
import banner2 from "../assets/banner2.png";
import banner3 from "../assets/banner3.png";
import banner4 from "../assets/banner4.png";

import CallToAction from "./callToAction";
import {
  FaArrowRight,
  FaBolt,
  FaBuilding,
  FaCheckCircle,
  FaCog,
  FaShieldAlt,
  FaSolarPanel,
  FaUsers,
} from "react-icons/fa";
import AOS from "aos";
import { AppContext } from "../App";
import "aos/dist/aos.css";

// ─────────────────────────────────────────────────────────────
// SERVICE DATA (clean structure)
// ─────────────────────────────────────────────────────────────
const services = [
  {
    id: 1,
    slug: "electrical-engineering",
    badge: "Power Systems",
    icon: <FaBolt />,
    gradient: "from-amber-400 to-orange-500",
    accent: "#f59e0b",
    title: "Electrical Engineering",
    shortDescription: "Complete LV/HV power solutions from design to commissioning.",
    description:
      "We deliver end-to-end electrical engineering services including high and low voltage installations, transformer setups, switchgear, testing, and maintenance.",
    features: [
      "LV & HV Installation & Maintenance",
      "Transformer Supply & Commissioning",
      "33kV/11kV Power Line Construction",
      "Testing, Metering & Protection Systems",
    ],
    stats: [
      { value: "250+", label: "Projects" },
      { value: "99.8%", label: "Safety" },
      { value: "-15%", label: "Time Saved" },
    ],
    image: banner1,
    imageAlt: "Electrical engineering project",
  },
  {
    id: 2,
    slug: "civil-engineering",
    badge: "Infrastructure",
    icon: <FaBuilding />,
    gradient: "from-blue-500 to-cyan-400",
    accent: "#3b82f6",
    title: "Civil Engineering",
    shortDescription: "Robust infrastructure built to last with precision engineering.",
    description:
      "Our civil engineering division handles structural works for substations, transmission towers, roads, and bridges.",
    features: [
      "Sub-Station Foundation Works",
      "Transmission Tower & Pole Installation",
      "Roads, Bridges & Drainage Systems",
      "Gabions & Soil Erosion Control",
    ],
    stats: [
      { value: "120+", label: "Km Roads" },
      { value: "85", label: "Structures" },
      { value: "5 yrs", label: "Zero Incidents" },
    ],
    image: banner2,
    imageAlt: "Civil engineering construction",
  },
  {
    id: 3,
    slug: "renewable-energy",
    badge: "Sustainability",
    icon: <FaSolarPanel />,
    gradient: "from-emerald-400 to-teal-500",
    accent: "#10b981",
    title: "Renewable Energy",
    shortDescription: "Sustainable power solutions for a greener future.",
    description:
      "We design and install solar PV systems and hybrid energy solutions tailored to your operational needs.",
    features: [
      "Solar PV System Design & Installation",
      "Hybrid Solar-Diesel Solutions",
      "Energy Audits & Feasibility Studies",
      "Battery Storage & Inverter Integration",
    ],
    stats: [
      { value: "45+", label: "MW Installed" },
      { value: "12k", label: "Tons CO₂" },
      { value: "~40%", label: "Client Savings" },
    ],
    image: banner3,
    imageAlt: "Solar installation project",
  },
  {
    id: 4,
    slug: "project-consultancy",
    badge: "Expertise",
    icon: <FaCog />,
    gradient: "from-violet-500 to-purple-400",
    accent: "#8b5cf6",
    title: "Project Management & Consultancy",
    shortDescription: "Strategic oversight from concept to completion.",
    description:
      "We provide technical advisory, planning, procurement support, and training to keep projects on time and on budget.",
    features: [
      "End-to-End Project Management",
      "Technical Feasibility & Design Review",
      "Procurement & Supply Chain Advisory",
      "Training & Knowledge Transfer",
    ],
    stats: [
      { value: "60+", label: "Projects" },
      { value: "96%", label: "On-Time" },
      { value: "92%", label: "Retention" },
    ],
    image: banner4,
    imageAlt: "Project consultancy session",
  },
];

const trustPoints = [
  {
    icon: <FaShieldAlt className="text-primary" />,
    title: "Fully Licensed",
    desc: "CRB Electrical & Civil, TANESCO Contractor, VAT/TIN Registered",
  },
  {
    icon: <FaUsers className="text-primary" />,
    title: "Expert Team",
    desc: "7+ certified engineers with 8-10 years average experience",
  },
  {
    icon: <FaCheckCircle className="text-primary" />,
    title: "Quality Assured",
    desc: "ISO-aligned processes with rigorous safety protocols",
  },
];

// ─────────────────────────────────────────────────────────────
// CLEAN PREMIUM COMPONENT
// ─────────────────────────────────────────────────────────────
export default function ServiceDetailContent({ serviceSlug }) {
  const [ready, setReady] = useState(false);
  const appContext = useContext(AppContext);
  
  const service = useMemo(() => 
    services.find((s) => s.slug === serviceSlug), 
    [serviceSlug]
  );

  useEffect(() => {
    AOS.init({ once: true, offset: 60, easing: "ease-out", duration: 500 });
    setReady(true);
  }, []);

  if (!ready || !service) {
    return (
      <div className="container py-5 min-vh-50 d-flex align-items-center justify-content-center">
        {getInlineLoader()}
      </div>
    );
  }

  return (
    <main className="service-detail-clean">
      
      {/* ── HERO ───────────────────────────────────────────── */}
      <section className="py-5 py-lg-6 bg-light">
        <div className="container">
          <div className="row g-4 g-lg-5 align-items-center">
            
            {/* Text Content */}
            <div className="col-lg-6" data-aos="fade-right">
              <span className={`badge bg-gradient ${service.gradient} text-white fw-medium px-3 py-2 mb-3`}>
                {service.badge}
              </span>
              <h1 className="display-5 fw-bold mb-3">{service.title}</h1>
              <p className="lead text-muted mb-4">{service.shortDescription}</p>
              
              {/* Stats Row */}
              <div className="d-flex flex-wrap gap-3 mb-4">
                {service.stats.map((stat, idx) => (
                  <div key={idx} className="d-flex align-items-baseline gap-1">
                    <span className="fw-bold" style={{ color: service.accent }}>{stat.value}</span>
                    <span className="text-muted small">{stat.label}</span>
                  </div>
                ))}
              </div>
              
              {/* CTA Buttons */}
              <div className="d-flex flex-wrap gap-3">
                <button
                    onClick={()=>appContext.navTo({item:'contact_us'})}
                    className={`btn btn-secondary btn-lg px-4 py-3 rounded-3 fw-medium text-white border-0 shadow-sm w-100 w-lg-auto`}
                  >
                    Get Quote<FaArrowRight className="ms-2 small" />
                  </button>
                <button
                    onClick={()=>appContext.navTo({item:'about_us'})}
                    className={`btn btn-lg btn-outline-secondary px-4 py-3 rounded-3 fw-medium text-white border-0 shadow-sm w-100 w-lg-auto`}
                  >
                    About Us<FaArrowRight className="ms-2 small" />
                </button>
              </div>
            </div>
            
            {/* Hero Image */}
            <div className="col-lg-6" data-aos="fade-left">
              <div className="rounded-4 overflow-hidden shadow-sm border">
                <img 
                  src={service.image} 
                  alt={service.imageAlt} 
                  className="img-fluid w-100"
                  style={{ aspectRatio: '16/10' }}
                  loading="lazy"
                />
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* ── TRUST BAR ───────────────────────────────────────── */}
      <section className="py-4 bg-white border-bottom">
        <div className="container">
          <div className="row g-4 justify-content-center">
            {trustPoints.map((item, i) => (
              <div key={i} className="col-md-4 col-lg-3" data-aos="fade-up" data-aos-delay={i * 100}>
                <div className="d-flex align-items-center gap-3">
                  <div className="flex-shrink-0 bg-light rounded-circle p-2 d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                    {item.icon}
                  </div>
                  <div>
                    <h6 className="mb-0 fw-medium">{item.title}</h6>
                    <small className="text-muted">{item.desc}</small>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICE DETAILS ─────────────────────────────────── */}
      <section id="details" className="py-5 py-lg-6 bg-white">
        <div className="container">
          <div className="row g-5">
            
            {/* Features List */}
            <div className="col-lg-6" data-aos="fade-right">
              <h2 className="h3 fw-bold mb-4">What's Included</h2>
              <div className="space-y-3">
                {service.features.map((feature, idx) => (
                  <div key={idx} className="d-flex align-items-start gap-3">
                    <FaCheckCircle className="text-success mt-1 flex-shrink-0" />
                    <p className="mb-0">{feature}</p>
                  </div>
                ))}
              </div>
              
              {/* Description */}
              <div className="mt-5 pt-4 border-top">
                <p className="text-muted mb-0">{service.description}</p>
              </div>
            </div>
            
            {/* Supporting Visual */}
            <div className="col-lg-6" data-aos="fade-left">
              <div className="rounded-4 overflow-hidden shadow-sm border h-100">
                <img 
                  src={service.image} 
                  alt={`${service.title} detail`} 
                  className="img-fluid w-100 h-100 object-fit-cover"
                  style={{ minHeight: '300px' }}
                  loading="lazy"
                />
              </div>
            </div>
            
          </div>
        </div>
      </section>

      {/* ── VALUE CARD ──────────────────────────────────────── */}
      <section className="py-5 py-lg-6 bg-light">
        <div className="container">
          <div className="card border-0 shadow-sm rounded-4" data-aos="fade-up">
            <div className="card-body p-4 p-lg-5">
              <div className="row g-4 align-items-center">
                <div className="col-lg-8">
                  <div className="d-flex align-items-center gap-3 mb-3">
                    <div className={`rounded-circle p-2 bg-gradient ${service.gradient} text-white`}>
                      {service.icon}
                    </div>
                    <h4 className="card-title mb-0 fw-bold">{service.title}</h4>
                  </div>
                  <p className="card-text text-muted mb-0">
                    Ready to move forward? Our team provides dedicated support from consultation through completion—with transparent pricing and clear timelines.
                  </p>
                </div>
                <div className="col-lg-4 text-lg-end">
                  <button
                    onClick={()=>appContext.navTo({item:'contact_us'})}
                    className={`btn btn-lg px-4 py-3 rounded-3 fw-medium text-white bg-primary border-0 shadow-sm w-100 w-lg-auto`}
                  >
                    Get Quote <FaArrowRight className="ms-2 small" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA ─────────────────────────────────────────────── */}
      <CallToAction />
      
      {/* ── MINIMAL GLOBAL STYLES ───────────────────────────── */}
      <style jsx global>{`
        /* Consistent spacing scale */
        .py-6 { padding-top: 4rem; padding-bottom: 4rem; }
        .space-y-3 > * + * { margin-top: 1rem; }
        
        /* Clean typography */
        .display-5 { font-size: 2rem; line-height: 1.2; }
        @media (min-width: 992px) { .display-5 { font-size: 2.5rem; } }
        
        /* Subtle shadows & borders */
        .shadow-sm { box-shadow: 0 2px 8px rgba(0,0,0,0.06); }
        .border { border-color: rgba(0,0,0,0.08) !important; }
        
        /* Gradient utility */
        .bg-gradient {
          background: linear-gradient(135deg, var(--tw-gradient-from), var(--tw-gradient-to));
        }
        
        /* Smooth interactions */
        .btn { transition: transform 0.15s ease, box-shadow 0.15s ease; }
        .btn:hover { transform: translateY(-1px); }
        
        /* Image handling */
        .object-fit-cover { object-fit: cover; }
        
        /* Reduce visual noise */
        .badge { font-weight: 500; letter-spacing: 0.01em; }
        small { font-weight: 400; }
      `}</style>
    </main>
  );
}